import { createContext, useContext, useMemo } from 'react';
import type { ApiClientConfig, ApiLogger, TokenProvider } from '@klareospace/pharos-lib-types';
import { ApiClient } from './api-client';

interface ApiClientProviderProps {
  children: React.ReactNode;
  /** Named API client configurations (e.g., { 'admin-auth': {...}, 'deliveries': {...} }) */
  configs: Record<string, ApiClientConfig>;
  /** Correlation ID prefix for this app (e.g., "bo", "fo", "drv") */
  correlationIdPrefix?: string;
  /** Optional logger shared by all instances */
  logger?: ApiLogger;
  /** Optional callback invoked every time any API client sends a request. Used to notify auth provider of backend activity. */
  onRequestSent?: () => void;
  /** Optional callback invoked on 401 responses. Used to trigger sign-out on session invalidation. */
  onUnauthorized?: () => void;
  /** Optional token provider shared by all instances */
  tokenProvider?: TokenProvider;
}

// Context holds a map of named ApiClient instances
const ApiClientContext = createContext<Record<string, ApiClient> | null>(null);

export function ApiClientProvider({
  children,
  configs,
  correlationIdPrefix,
  logger,
  onRequestSent,
  onUnauthorized,
  tokenProvider,
}: ApiClientProviderProps): React.JSX.Element {
  const clients = useMemo(() => {
    return Object.fromEntries(
      Object.entries(configs).map(([name, config]) => [
        name,
        new ApiClient({ config, correlationIdPrefix, logger, onRequestSent, onUnauthorized, tokenProvider }),
      ]),
    );
  }, [configs, correlationIdPrefix, logger, onRequestSent, onUnauthorized, tokenProvider]);

  return <ApiClientContext.Provider value={clients}>{children}</ApiClientContext.Provider>;
}

/**
 * Hook to access a named API client instance.
 * @param name Instance name (e.g., 'admin-auth', 'deliveries')
 */
export function useApiClient(name: string): ApiClient {
  const clients = useContext(ApiClientContext);

  if (!clients) {
    throw new Error('useApiClient must be used within <ApiClientProvider>');
  }

  const client = clients[name];

  if (!client) {
    throw new Error(`API client "${name}" not found. Available: ${Object.keys(clients).join(', ')}`);
  }

  return client;
}
